import { useState, useCallback } from 'react';
import { useVirtualKitchen } from './useVirtualKitchen';
import { useSound } from './useSound';
import { useProducts } from '../context/ProductContext';
import { generateRecipeSteps } from '../utils/grokApi';
import { MenuOptions } from '../types/Menu';
import { DEFAULT_MENU_OPTIONS } from '../config/constants';
import { generateBabyMenu } from '../utils/baby/menuGenerator';

export const useMenuGeneration = () => {
  const { products } = useProducts();
  const { generateRandomRecipe, generateBasicRecipe } = useVirtualKitchen();
  const { playSound } = useSound();

  const [currentMenu, setCurrentMenu] = useState(null);
  const [recipeSteps, setRecipeSteps] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingRecipe, setIsGeneratingRecipe] = useState(false);
  const [useAI, setUseAI] = useState(true);
  const [isBabyMenu, setIsBabyMenu] = useState(false);
  const [selectedBabyAge, setSelectedBabyAge] = useState('4-6');
  const [babyMenuOptions, setBabyMenuOptions] = useState({
    maxPrice: 20,
    courseCount: 1
  });
  const [menuOptions, setMenuOptions] = useState<MenuOptions>(DEFAULT_MENU_OPTIONS);

  const handleGenerateMenu = useCallback(async () => {
    if (!products || products.length === 0) {
      console.error('No products available');
      return;
    }

    setIsLoading(true);
    playSound('click');
    
    try {
      let menu;
      if (isBabyMenu) {
        menu = await generateBabyMenu(
          products, 
          selectedBabyAge, 
          babyMenuOptions.maxPrice, 
          babyMenuOptions.courseCount
        );
      } else {
        menu = useAI ? 
          generateRandomRecipe(menuOptions.maxPricePerDish, menuOptions.courseCount) : 
          generateBasicRecipe(menuOptions.maxPricePerDish, menuOptions.courseCount);
      }
      
      setCurrentMenu(menu);

      if (menuOptions.showPreparation && useAI && !isBabyMenu) {
        setIsGeneratingRecipe(true);
        const menuProducts = menu.items
          ?.flatMap(item => item.products)
          .map(id => products.find(p => p.id === id))
          .filter(Boolean) || [];

        const steps = await generateRecipeSteps(menuProducts, menu.name, menuOptions);
        setRecipeSteps(steps);
      }
    } catch (error) {
      console.error('Error generating menu:', error);
    } finally {
      setIsLoading(false);
      setIsGeneratingRecipe(false);
    }
  }, [useAI, isBabyMenu, selectedBabyAge, babyMenuOptions, menuOptions, generateRandomRecipe, generateBasicRecipe, products, playSound]);

  const updateBabyMenuOption = useCallback((option: keyof typeof babyMenuOptions, value: number) => {
    setBabyMenuOptions(prev => ({
      ...prev,
      [option]: value
    }));
  }, []);

  return {
    currentMenu,
    recipeSteps,
    isLoading,
    isGeneratingRecipe,
    useAI,
    isBabyMenu,
    selectedBabyAge,
    babyMenuOptions,
    menuOptions,
    handleGenerateMenu,
    setUseAI,
    setIsBabyMenu,
    setSelectedBabyAge,
    updateBabyMenuOption,
    toggleOption: useCallback((option: keyof MenuOptions) => {
      setMenuOptions(prev => ({
        ...prev,
        [option]: !prev[option]
      }));
    }, []),
    updateOption: useCallback((option: keyof MenuOptions, value: any) => {
      setMenuOptions(prev => ({
        ...prev,
        [option]: value
      }));
    }, []),
  };
};